<template>
  <form method="post" @submit.prevent="send" novalidate>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title is-centered">
          {{ $t('attributes.copy') + $t('attributes.collaborate_group') }}
        </p>
      </header>
      <section class="modal-card-body">
        <form-wrapper :validator="$v.form" style="min-height: 600px">
          <b-field grouped>
            <b-field expanded>
              <template #label>
                {{ $t('attributes.collaborate_group') }}
                <!-- eslint-disable-next-line-->
                <span v-if="selectedGroupName" v-html="': ' + selectedGroupName"></span>
              </template>
              <form-group name="group_code" expanded>
                <b-autocomplete
                  ref="group_code_input"
                  v-model="group_code"
                  name="group_code"
                  field="code"
                  :data="groups"
                  :loading="isLoadingGroups"
                  :placeholder="$t('message.please_input_group_code')"
                  :open-on-focus="false"
                  @typing="loadGroups"
                  @select="onSelectGroup"
                >
                  <template #default="props">
                    <p>
                      {{ props.option.code }} -
                      <!-- eslint-disable-next-line-->
                      <span v-html="props.option.name"></span>
                    </p>
                  </template>
                  <template #empty>
                    {{
                      group_code
                        ? isLoadingGroups
                          ? $t('attributes.searching')
                          : $t('attributes.not_found')
                        : $t('message.please_input_group_code')
                    }}
                  </template>
                </b-autocomplete>
              </form-group>
            </b-field>

            <form-group
              name="serial"
              attribute="attributes.group_serial"
              :label="$t('attributes.group_serial')"
            >
              <b-select
                v-model="form.serial"
                name="serial"
                :placeholder="
                  $t('message.please_select') + $t('attributes.group_serial')
                "
                expanded
              >
                <option
                  v-for="option in group_serials"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>
          </b-field>

          <form-group name="quantity" :label="$t('attributes.quantity')">
            <b-input
              v-model.number="form.quantity"
              type="number"
              name="quantity"
              min="1"
              :placeholder="
                $t('message.please_input') + $t('attributes.quantity')
              "
              :open-on-focus="true"
            >
            </b-input>
          </form-group>

          <form-group
            name="departure_date"
            :label="$t('attributes.departure_date')"
            expanded
          >
            <b-datepicker
              v-model="departure_date"
              name="departure_date"
              :placeholder="
                $t('message.please_input') + $t('attributes.departure_date')
              "
              :min-date="min_date"
              :mobile-native="false"
              :date-formatter="formatDate"
              position="is-bottom-right"
              @input="onSelectDepartureDate"
            >
            </b-datepicker>
          </form-group>

          <form-group
            name="registration_range"
            attribute="attributes.registration_range"
            :label="$t('attributes.registration_range')"
            expanded
          >
            <b-datepicker
              v-model="registration_range"
              name="registration_range"
              :date-formatter="formatDates"
              :min-date="min_date"
              :max-date="registration_max_date"
              :mobile-native="false"
              :placeholder="
                $t('message.please_input') + $t('attributes.registration_range')
              "
              position="is-bottom-right"
              icon="calendar-today"
              trap-focus
              range
              @input="onSelectRegistrationRange"
            >
            </b-datepicker>
          </form-group>

          <form-group
            name="signup_deadline"
            :label="$t('attributes.signup_deadline')"
            expanded
          >
            <b-datepicker
              v-model="signup_deadline"
              name="signup_deadline"
              :placeholder="
                $t('message.please_input') + $t('attributes.signup_deadline')
              "
              :min-date="deadline_min_date"
              :max-date="deadline_max_date"
              :mobile-native="false"
              :date-formatter="formatDate"
              position="is-bottom-right"
              @input="onSelectSignupDeadline"
            >
            </b-datepicker>
          </form-group>
        </form-wrapper>
      </section>
      <footer class="modal-card-foot">
        <b-button :loading="isSending" @click="$parent.close()">
          {{ $t('attributes.cancel') }}
        </b-button>
        <b-button
          :loading="isSending || isLoading"
          type="is-primary"
          native-type="submit"
        >
          {{ $t('attributes.copy') }}
        </b-button>
      </footer>
    </div>
  </form>
</template>

<script>
import debounce from 'lodash/debounce'
import {
  required,
  numeric,
  minValue,
  maxValue,
  between
} from 'vuelidate/lib/validators'
import { TRAVEL_GROUPS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'TravelGroupCloneForm',

  props: {
    isSending: {
      type: Boolean,
      default: true
    }
  },
  data() {
    const minDate = this.$dayjs()
      .startOf('day')
      .toDate()

    return {
      group_serials: ['U', 'V', 'W', 'X', 'Y', 'Z'],

      isLoadingGroups: false,
      groups: [],
      selectedGroupName: null,

      min_date: minDate,
      departure_date: null,
      registration_range: [],
      signup_deadline: null,

      group_code: null,
      form: {
        serial: 'U',
        quantity: 0,
        departure_date: null,
        registration_start_date: null,
        registration_end_date: null,
        signup_deadline: null
      }
    }
  },
  validations() {
    return {
      group_code: { required },
      form: {
        serial: { required },
        quantity: { required, numeric, minValue: minValue(1) },
        departure_date: { required },
        registration_start_date: { required },
        registration_end_date: {
          required,
          maxValue: maxValue(this.departure_date)
        },
        signup_deadline: {
          required,
          between: between(
            this.form.registration_start_date,
            this.form.registration_end_date
          )
        }
      }
    }
  },
  computed: {
    isLoading() {
      return this.isLoadingGroups
    },
    registration_max_date() {
      if (!this.departure_date) {
        return null
      }

      return this.$dayjs(this.departure_date)
        .subtract(1, 'day')
        .toDate()
    },
    deadline_min_date() {
      if (this.form.registration_start_date) {
        return this.form.registration_start_date
      }

      return this.min_date
    },
    deadline_max_date() {
      if (this.form.registration_end_date) {
        return this.form.registration_end_date
      }

      if (this.form.departure_date) {
        return this.$dayjs(this.form.departure_date)
          .subtract(1, 'day')
          .toDate()
      }

      return null
    }
  },
  mounted() {
    this.$refs.group_code_input.focus()
  },
  methods: {
    async loadAsyncGroups() {
      const api = `${TRAVEL_GROUPS_API}/search/collaborate`
      const params = {
        code: this.group_code
      }

      this.isLoadingGroups = true
      try {
        const res = await this.axios.get(api, { params })
        this.groups = res.data.data
      } catch (e) {
        this.groups = []

        notifyService.fetch_error('collaborate_group', e)
      } finally {
        this.isLoadingGroups = false
      }
    },
    loadGroups: debounce(function() {
      if (!this.group_code) {
        this.groups = []
        return
      }

      this.loadAsyncGroups()
    }, 1000),
    onSelectGroup(option) {
      this.selectedGroupName = option && option.name
    },
    onSelectDepartureDate() {
      this.form.departure_date = this.departure_date
    },
    onSelectSignupDeadline() {
      this.form.signup_deadline = this.signup_deadline
    },
    onSelectRegistrationRange() {
      this.form.registration_start_date = this.registration_range[0]
      this.form.registration_end_date = this.registration_range[1]
    },
    async send() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      const form = {
        ...this.form,
        departure_date: this.formatDate(this.form.departure_date),
        signup_deadline: this.formatDate(this.form.signup_deadline),
        registration_start_date: this.formatDate(
          this.form.registration_start_date
        ),
        registration_end_date: this.formatDate(this.form.registration_end_date)
      }

      this.$emit('onSend', this.group_code, form)
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  min-height: 480px;
}
</style>
